<script setup lang="ts">
import { computed, ref } from 'vue'

import MediaImage from '@fondof-ui/components/media/MediaImage.vue'
import MediaRoot from '@fondof-ui/components/media/MediaRoot.vue'
import { contentfulUrl } from '@internal/format-contentful'
import Button from '@satch-ui/components/button/Button.vue'
import HeroContent from '@storefront-satch/components/molecules/hero/HeroContent.vue'
import HeroRoot from '@storefront-satch/components/molecules/hero/HeroRoot.vue'
import type { BrandHero } from '@storefront-satch/components/molecules/hero/types'
import MediaVideo from '@storefront-satch/components/molecules/media/MediaVideo.vue'
import { clsx } from 'clsx'

import { ArrowRightIcon, PlayIcon } from '@fond-of/satch-icons'

interface HeroFullProps extends BrandHero {
  /**
   * The Media type is used to differentiate between video and image to enable certain behaviours
   */
  mediaType?: 'image' | 'video' | 'additionalVideo'
}

const props = defineProps<HeroFullProps>()
const hasAdditionalImage = !!props.additionalImage
const isVideoPlaying = ref<boolean>(false)

const isLinkElement = computed(() => {
  return props.link && !isVideoWithoutAutoplay.value
})

const isVideoWithoutAutoplay = computed(() => {
  return props.mediaType === 'video' && !props.media?.videoAutoplay
})

const hasVideo = computed(() => {
  return props.mediaType === 'video' || props.mediaType === 'additionalVideo'
})
</script>
<template>
  <HeroRoot
    v-bind="props"
    data-label="Hero"
    :hero-id="props.sys?.id"
    class="2xl:min-h-none sm:min-h-[43.9vw] 2xl:h-[calc(theme(screens[2xl])*0.439)]"
    :has-additional-image="hasAdditionalImage"
  >
    <component
      :is="isLinkElement ? 'a' : 'div'"
      data-label="Hero Inner"
      :href="isLinkElement ? contentfulUrl(props.link) : undefined"
      :class="[
        'gap-7.5 relative flex size-full flex-col flex-nowrap justify-between',
        'sm:min-h-[43.9vw] sm:flex-row sm:items-center sm:gap-0',
        '2xl:h-[calc(theme(screens[2xl])*0.439)]',
      ]"
      :data-gtm-attr="isLinkElement ? 'button_hero' : undefined"
      :data-gtm-label="isLinkElement ? props.internalTitle : undefined"
      :aria-label="
        isLinkElement
          ? props.linkAriaLabel?.trim() || props.linkText?.trim() || undefined
          : undefined
      "
    >
      <div
        v-if="props.media"
        data-label="Hero Image"
        :class="
          clsx(
            'relative aspect-square w-full self-start',
            'sm:order-2 sm:aspect-auto sm:min-h-[43.9vw] sm:w-1/2',
            'lg:w-3/5',
            {
              'mb-10 sm:mb-0': hasAdditionalImage,
              'md:w-full': props.mediaType === 'video',
            }
          )
        "
      >
        <MediaRoot
          :entry="props.media"
          :class="[
            'media absolute left-0 top-0 size-full object-top',
            {
              'sm:object-left-top': hasAdditionalImage,
            },
          ]"
        >
          <template #video="{ data }">
            <MediaVideo
              v-bind="data"
              class="size-full"
              @update:video-playing="
                (state) => {
                  isVideoPlaying = state
                }
              "
            />
          </template>
          <template #image="{ data }">
            <MediaImage
              v-bind="data"
              :class="[
                'size-full',
                {
                  'sm:object-cover': !hasAdditionalImage,
                  'sm:object-[top_left]': hasAdditionalImage,
                },
              ]"
              :divider-mobile="2"
              :divider-tablet="1.5"
              :divider-desktop="1"
              lazyload="eager"
              fetchpriority="high"
            />
          </template>
        </MediaRoot>
      </div>
      <div
        data-label="content wrapper"
        :class="[
          'relative w-full pl-5 pr-5',
          'sm:w-[calc(50%-theme(space.5))] sm:pl-10 sm:pr-0',
          'md:w-[calc(50%-theme(space.10))]',
          'lg:pl-15 lg:w-[calc(40%-theme(space.15))]',
          'xl:w-[calc(40%-theme(space.20))]',
          {
            'relative order-2 sm:order-none': hasAdditionalImage,
          },
        ]"
      >
        <div
          v-if="props.content"
          :class="[
            'select-none transition-opacity duration-100',
            {
              'opacity-1': mediaType === 'image',
              'cursor-pointer':
                mediaType === 'video' && !props.media?.videoAutoplay,
              'opacity-0':
                mediaType === 'video' &&
                isVideoPlaying === true &&
                !props.media?.videoAutoplay,
            },
          ]"
        >
          <HeroContent
            :content="props.content"
            :content-style="props.contentStyle"
            :has-additional-image="hasAdditionalImage"
            :headline-size="props.headlineSize"
            :headline-style="props.headlineStyle"
            class-headline="text-2xl sm:text-xl md:text-2xl"
          />
          <div
            v-if="props.linkStyle && props.linkText"
            class="mt-4 w-full"
          >
            <Button
              v-if="props.linkText && (props.link || isVideoWithoutAutoplay)"
              class="font-sans-medium font-semibold"
              :variant="props.linkStyle"
              padding="large"
              text-size="large"
            >
              {{ props.linkText }}
              <PlayIcon
                v-if="isVideoWithoutAutoplay && hasVideo"
                class="w-4"
              />
              <ArrowRightIcon
                v-else
                class="w-4"
              />
            </Button>
          </div>
        </div>
      </div>
      <div
        v-if="hasAdditionalImage"
        :class="
          clsx(
            'absolute left-1/2 top-[100px] z-[1] aspect-square w-[calc(100%-60px)] -translate-x-1/2',
            'sm:left-auto sm:top-1/2 sm:h-4/5 sm:w-auto sm:-translate-y-1/2 sm:translate-x-0',
            'sm:right-15 lg:right-25 md:right-20'
          )
        "
      >
        <MediaRoot
          :entry="props.additionalImage"
          class="relative h-full w-full"
        >
          <template #video="{ data }">
            <MediaVideo
              v-bind="data"
              class="h-full w-full"
              @update:video-playing="
                (state) => {
                  isVideoPlaying = state
                }
              "
            />
          </template>
          <template #image="{ data }">
            <MediaImage
              v-bind="data"
              class="w-full"
              :divider-mobile="2"
              :divider-tablet="2"
              :divider-desktop="1.5"
              lazyload="eager"
              fetchpriority="high"
            />
          </template>
        </MediaRoot>
      </div>
    </component>
  </HeroRoot>
</template>
